/**
 * Rahmen des Control Panels: Seitenleiste, Kopfzeile, Inhaltsbereich.
 *
 * Die Kopfzeile trägt die Simulationssteuerung, weil Zeit die wichtigste
 * Ressource des Spiels ist und in jeder Ansicht erreichbar bleiben muss.
 */
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Badge, Button, Spinner, StatusDot } from '../ui';
import { useCanControlTime, useGame, useMetrics, useUnlocked } from '../../store/game';
import { useAreaVisibility, useAuth, useTheme } from '../../store/auth';
import { gameDate, money, num, pct } from '../../lib/format';
import { ToastStack } from './ToastStack';
import { EventBanner } from './EventBanner';

export function Shell() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { open, close, loading, error } = useGame();

  useEffect(() => {
    if (!gameId) return;
    void open(gameId);
    return () => close();
  }, [gameId, open, close]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-3 text-sm text-panel-500">
        <Spinner /> Partie wird geladen …
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={() => navigate('/')}>Zurück zur Übersicht</Button>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar />
        <EventBanner />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      <ToastStack />
    </div>
  );
}

const TICK_RATE_PER_SPEED = 0.02;
const TICKS_PER_DAY = 24;

/** Wie lange ein Spieltag bei dieser Stufe in Echtzeit dauert. */
function gameDayDuration(speed: number): string {
  const seconds = TICKS_PER_DAY / (speed * TICK_RATE_PER_SPEED);
  if (seconds < 90) return `${Math.round(seconds)} s`;
  return `${(seconds / 60).toFixed(1).replace('.', ',')} min`;
}

function Topbar() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const metrics = useMetrics();
  const { setRunning, fastForward } = useGame();
  const { dark, toggle } = useTheme();
  const [busy, setBusy] = useState(false);
  const canControlTime = useCanControlTime();

  const paused = metrics?.meta.paused ?? true;
  const speed = metrics?.meta.speed ?? 1;
  const cash = metrics?.company.cash ?? 0;
  const profit = metrics?.derived.finance.monthlyProfit ?? 0;

  // Wer nur Tickets oder nur die Kundensicht hat, soll den Stand jederzeit
  // ablesen, aber weder pausieren noch vorspulen können. Die Schaltflächen
  // bleiben sichtbar – sie zeigen ja auch den Status –, sind aber gesperrt,
  // statt beim Klick erst über den Server zu scheitern.
  const timeControlTitle = canControlTime ? undefined : 'Nur Administratoren und Techniker steuern die Zeit.';

  const setSpeed = async (value: number) => {
    setBusy(true);
    await setRunning(true, value);
    setBusy(false);
  };

  return (
    <header className="flex flex-wrap items-center gap-3 border-b divider bg-white px-4 py-2.5 dark:bg-panel-900">
      <Button variant="secondary" size="sm" onClick={() => gameId && navigate(`/game/${gameId}/zuhause`)}>
        ← Standort
      </Button>
      <Button size="sm" variant="ghost" onClick={() => navigate('/')}>
        Partien
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={async () => {
          await useAuth.getState().logout();
          navigate('/login');
        }}
      >
        Abmelden
      </Button>

      {/* Zeitsteuerung */}
      <div className="flex items-center gap-1.5">
        <Button
          size="sm"
          variant={paused ? 'primary' : 'secondary'}
          loading={busy}
          disabled={!canControlTime}
          title={timeControlTitle}
          onClick={async () => {
            setBusy(true);
            await setRunning(paused, paused ? speed || 1 : undefined);
            setBusy(false);
          }}
        >
          {paused ? '▶ Start' : '❚❚ Pause'}
        </Button>
        <div className="hidden items-center gap-0.5 rounded-md border border-panel-300 p-0.5 dark:border-panel-700 sm:flex">
          {[1, 2, 4, 8, 16].map((value) => (
            <button
              key={value}
              onClick={() => void setSpeed(value)}
              disabled={!canControlTime}
              title={canControlTime ? `${value}× – ein Spieltag in ${gameDayDuration(value)}` : timeControlTitle}
              className={clsx(
                'rounded px-1.5 py-0.5 text-2xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                !paused && speed === value
                  ? 'bg-accent-600 text-white'
                  : 'text-panel-500 hover:bg-panel-100 dark:text-panel-400 dark:hover:bg-panel-800',
              )}
            >
              {value}×
            </button>
          ))}
        </div>
        {/*
          Drei Sprungweiten statt einer.
          Ein ganzer Tag ist oft zu viel: Wer auf eine Lieferung, einen Umzug
          oder eine fertige Strecke wartet, will die Stunden dazwischen nicht
          verlieren – aber auch nicht 24-mal auf Weiter drücken.
        */}
        <div className="flex items-center gap-0.5 rounded-md border border-panel-300 p-0.5 dark:border-panel-700">
          {[
            { ticks: 6, label: '+6 h', title: 'Sechs Stunden vorspulen' },
            { ticks: 12, label: '+12 h', title: 'Einen halben Spieltag vorspulen' },
            { ticks: 24, label: '+1 Tag', title: 'Einen Spieltag vorspulen' },
          ].map((step) => (
            <button
              key={step.ticks}
              onClick={() => void fastForward(step.ticks)}
              disabled={!canControlTime}
              title={canControlTime ? step.title : timeControlTitle}
              className="rounded px-1.5 py-0.5 text-2xs font-medium text-panel-600 transition-colors hover:bg-panel-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:text-panel-300 dark:hover:bg-panel-800"
            >
              {step.label}
            </button>
          ))}
        </div>
      </div>

      <div className="hidden h-6 w-px bg-panel-200 dark:bg-panel-800 md:block" />

      {/* Kennzahlen */}
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-5 gap-y-1 text-xs">
        <span className="tabular text-panel-500 dark:text-panel-400">{metrics ? gameDate(metrics.meta.date) : '–'}</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="label">Kasse</span>
          <span className={clsx('tabular font-semibold', cash < 0 ? 'text-red-600 dark:text-red-400' : '')}>{money(cash, true)}</span>
        </span>
        <span className="hidden items-center gap-1.5 sm:inline-flex">
          <span className="label">Ergebnis</span>
          <span className={clsx('tabular font-semibold', profit < 0 ? 'text-red-600 dark:text-red-400' : 'text-accent-600 dark:text-accent-400')}>
            {money(profit, true)}
          </span>
        </span>
        <span className="hidden items-center gap-1.5 md:inline-flex">
          <span className="label">Kunden</span>
          <span className="tabular font-semibold">{num(metrics?.derived.customers.total ?? 0)}</span>
        </span>
        <span className="hidden items-center gap-1.5 lg:inline-flex">
          <span className="label">Erreichbarkeit</span>
          <StatusDot
            tone={
              (metrics?.derived.internetReachability ?? 0) > 0.98
                ? 'success'
                : (metrics?.derived.internetReachability ?? 0) > 0.5
                  ? 'warning'
                  : 'danger'
            }
            label={pct(metrics?.derived.internetReachability ?? 0, 0)}
          />
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        {!paused && <Badge tone="success">läuft</Badge>}
        <Button variant="ghost" size="sm" onClick={toggle} aria-label="Farbschema wechseln" title="Hell / Dunkel">
          {dark ? '☀' : '☾'}
        </Button>
      </div>
    </header>
  );
}
